import { useUserStore } from "@/stores/userStore";
import { SelectItem } from "@repo/ui/components/Base/Select";
import type { RoleType } from "@repo/types";
import { capitalize } from "@repo/shared/utils";
import {
  Breadcrumb,
  BreadcrumbItem,
} from "@repo/ui/components/Base/Breadcrumb";
import { Link, type Params, useMatches, useNavigate } from "react-router-dom";
import { Tooltip } from "@repo/ui/components/Base/Tooltip";
import { InfoIcon } from "lucide-react";
import { SelectInput } from "@repo/ui/components/Base/SelectInput";
import { useState } from "react";
import { RoleDefinitionsDialog } from "../common/RoleDefinitionsDialog";

interface IMatches {
  id: string;
  pathname: string;
  params: Params<string>;
  data: unknown;
  handle: unknown;
}

type HandleType = {
  crumb: (param?: string) => React.ReactNode;
};

function Breadcrumbs() {
  const matches: IMatches[] = useMatches();
  const crumbs = matches
    .filter((match) => !!(match.handle as HandleType)?.crumb)
    .map((match) => {
      const crumb = (match.handle as HandleType).crumb(
        match.data as string | undefined
      );
      return { crumb: crumb as React.ReactNode, match };
    });

  return (
    <Breadcrumb>
      {crumbs.map(({ crumb, match }, index) => (
        <BreadcrumbItem key={index}>
          <Link
            className="max-w-[250px] overflow-hidden text-ellipsis"
            to={match.pathname}
          >
            {crumb}
          </Link>
        </BreadcrumbItem>
      ))}
    </Breadcrumb>
  );
}

export const ControlBar = ({ teamDisabled }: { teamDisabled: boolean }) => {
  const {
    activeTenant,
    activeTeam,
    activeRole,
    availableTenants,
    availableTeams,
    availableRoles,
    user,
    setActiveTenant,
    setActiveTeam,
    setActiveRole,
  } = useUserStore((s) => s);
  const navigate = useNavigate();

  const [roleDetailsOpen, setRoleDetailsOpen] = useState(false);
  const showFullOrganization =
    !!activeTenant &&
    !!user &&
    (activeTenant.viewUsers.includes(user.id) ||
      activeTenant.submitUsers.includes(user.id) ||
      activeTenant.editUsers.includes(user.id) ||
      activeTenant.adminUsers.includes(user.id));
  const teamSelectDisabled =
    teamDisabled ||
    !activeTenant ||
    (!showFullOrganization && availableTeams.length === 0);
  const teamSelectValue = activeTenant
    ? activeTeam === "org"
      ? showFullOrganization
        ? activeTeam
        : undefined
      : availableTeams.some((team) => team.id === activeTeam?.id)
        ? activeTeam?.id
        : undefined
    : undefined;
  const teamSelectPlaceholder = !activeTenant
    ? "Select Organization First"
    : !showFullOrganization && availableTeams.length === 0
      ? "No Teams Available"
      : "Select a Team";

  if (
    !(
      availableTenants.length > 0 ||
      availableTeams.length > 0 ||
      availableRoles.length > 0
    ) &&
    activeTenant &&
    activeTeam &&
    activeRole
  ) {
    return null;
  }

  return (
    <div className="p-2 flex flex-row flex-wrap gap-y-3 align-middle">
      <RoleDefinitionsDialog
        open={roleDetailsOpen}
        setOpen={setRoleDetailsOpen}
      />
      <div className="my-auto whitespace-nowrap grow w-[250px]">
        <Breadcrumbs />
      </div>
      <div className="w-4" />
      <div className="flex flex-row gap-2">
        <Tooltip label="Active Organization" asChild>
          <div>
            <SelectInput
              containerStyle="mt-0"
              triggerStyle="max-w-[250px] shrink-0 border-transparent focus:ring-1"
              placeholder="Select an Organization"
              value={activeTenant?.id}
              onValueChange={(value) => {
                if (value === "new") {
                  navigate("/setup/firsttime?stepId=1");
                  return;
                }
                setActiveTenant(
                  availableTenants?.find((tenant) => tenant.id === value)
                );
                navigate(
                  `/app/tenant/${
                    availableTenants?.find((tenant) => tenant.id === value)
                      ?.friendlyId
                  }`
                );
              }}
              options={[
                ...availableTenants.map((lh) => (
                  <SelectItem key={lh.id} value={lh.id}>
                    {lh.name}
                  </SelectItem>
                )),
                <SelectItem key="new" value="new">
                  + New Organization
                </SelectItem>,
              ]}
            />
          </div>
        </Tooltip>

        <Tooltip label="Active Team" asChild>
          <div>
            <SelectInput
              disabled={teamSelectDisabled}
              containerStyle="mt-0"
              triggerStyle="max-w-[250px] shrink-0 border-transparent focus:ring-1"
              placeholder={teamSelectPlaceholder}
              value={teamSelectValue}
              onValueChange={(value) =>
                setActiveTeam(
                  value === "org"
                    ? "org"
                    : availableTeams?.find((team) => team.id === value)
                )
              }
              options={[
                ...(showFullOrganization
                  ? [
                      <SelectItem key="org" value="org">
                        Full Organization
                      </SelectItem>,
                    ]
                  : []),
                ...availableTeams.map((lh) => (
                  <SelectItem key={lh.id} value={lh.id}>
                    {lh.name}
                  </SelectItem>
                )),
              ]}
            />
          </div>
        </Tooltip>

        <Tooltip label="Active Role" asChild>
          <div>
            <SelectInput
              containerStyle="mt-0"
              triggerStyle="max-w-[250px] shrink-0 border-transparent focus:ring-1"
              placeholder="Select a Role"
              value={activeRole}
              onValueChange={(value: string) => {
                if (value === "readmore") {
                  setRoleDetailsOpen(true);
                } else {
                  setActiveRole(value as RoleType);
                }
              }}
              options={[
                ...availableRoles.map((role) => (
                  <SelectItem key={role} value={role}>
                    {capitalize(role)}
                  </SelectItem>
                )),
                <SelectItem key="readmore" value="readmore">
                  Role Details
                  <InfoIcon
                    size={20}
                    className="inline-block absolute left-1.5"
                  />
                </SelectItem>,
              ]}
            />
          </div>
        </Tooltip>
      </div>
    </div>
  );
};
